import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Upload, UserPlus, Trash2, ArrowLeft, Download, Search, Mail, Building2, ShieldOff } from "lucide-react";

const initialsFor = (name = "", email = "") => {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

export default function ListDetail() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get(`/lists/${id}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [id]);

  const onAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/lists/${id}/contacts`, form);
      toast.success("Contact added"); setOpen(false); setForm({ name: "", email: "", company: "" }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/lists/${id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} contacts (${data.skipped} skipped)`);
      load();
    } catch (err) { toast.error("Import failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDelete = async (cid) => {
    if (!window.confirm("Remove contact?")) return;
    await api.delete(`/contacts/${cid}`); load();
  };

  const downloadTemplate = () => {
    const csv = "name,email,company\nJane Doe,jane@example.com,Acme Inc\nJohn Smith,john@startup.io,Startup Co\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "contacts_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!list) return <div className="p-12 text-zinc-500 font-mono text-sm">// loading...</div>;

  const contacts = list.contacts || [];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? contacts.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q),
      )
    : contacts;

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] cf-fade">
      <Link to="/lists" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-2 mb-4" data-testid="back-to-lists">
        <ArrowLeft size={12} /> Back to lists
      </Link>
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-zinc-800 flex-wrap gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// list_id: {list.id.slice(0, 8)}</div>
          <h1 className="cf-page-title mt-2" data-testid="list-name">{list.name}</h1>
          {list.description && <p className="text-zinc-500 mt-2 max-w-2xl text-sm">{list.description}</p>}
          <div className="mt-3 font-mono text-xs text-zinc-400">{contacts.length} contacts</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="cf-btn-secondary" data-testid="download-csv-template">
            <Download size={12} /> CSV Template
          </button>
          <label className="cf-btn-secondary cursor-pointer" data-testid="import-csv-dropzone">
            <Upload size={12} /> {uploading ? "Importing..." : "Import CSV"}
            <input ref={fileRef} type="file" accept=".csv" onChange={onUpload} className="hidden" />
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-contact-button" className="cf-btn-primary"><UserPlus size={14} /> Add Contact</button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white">
              <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">Add Contact</DialogTitle></DialogHeader>
              <form onSubmit={onAdd} className="space-y-4 mt-4">
                <input data-testid="contact-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" />
                <input data-testid="contact-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cf-input" />
                <input data-testid="contact-company" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="cf-input" />
                <DialogFooter><button type="submit" data-testid="submit-add-contact" className="cf-btn-primary w-full justify-center">Add Contact</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {contacts.length === 0 ? (
        <div className="cf-card p-16 text-center border-dashed">
          <Upload size={32} className="mx-auto text-zinc-700 mb-4" strokeWidth={1.25} />
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">// no contacts yet</div>
          <div className="text-zinc-600 text-sm mt-2">Import a CSV with columns: name, email, company</div>
        </div>
      ) : (
        <div className="cf-card overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap bg-[#0A0A0A]">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or company..."
                className="cf-input"
                style={{ paddingLeft: 34 }}
                data-testid="contact-search-input"
              />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {filtered.length} of {contacts.length} contacts
            </div>
          </div>

          {/* SendGrid-style fixed-layout table */}
          <div className="overflow-x-auto">
            <table className="cf-data-table" style={{ minWidth: 720 }} data-testid="contacts-table">
              <colgroup>
                <col style={{ width: "44%" }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th style={{ textAlign: "right" }}>·</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-zinc-600 py-10 font-mono text-xs uppercase">
                      // no matches
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => {
                    const initials = initialsFor(c.name, c.email);
                    const unsub = c.unsubscribed;
                    return (
                      <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                        <td title={c.name}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="cf-avatar" aria-hidden="true">{initials}</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-white truncate flex items-center gap-2">
                                <span className="truncate">{c.name || <em className="text-zinc-600">No name</em>}</span>
                                {unsub && (
                                  <span
                                    title="Unsubscribed"
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-500/40 text-amber-400 text-[9px] font-mono uppercase tracking-widest"
                                  >
                                    <ShieldOff size={9} /> unsub
                                  </span>
                                )}
                              </div>
                              <div className="cf-cell-secondary truncate flex items-center gap-1">
                                <Mail size={10} className="opacity-60" />
                                <span className="truncate">{c.email}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td title={c.email}>
                          <span className="font-mono text-xs text-zinc-400 truncate block">{c.email}</span>
                        </td>
                        <td title={c.company || ""}>
                          <span className="inline-flex items-center gap-1.5 text-zinc-400 truncate">
                            {c.company ? (
                              <>
                                <Building2 size={11} className="opacity-60 flex-shrink-0" />
                                <span className="truncate">{c.company}</span>
                              </>
                            ) : (
                              <span className="text-zinc-700">—</span>
                            )}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            onClick={() => onDelete(c.id)}
                            data-testid={`delete-contact-${c.id}`}
                            className="text-zinc-600 hover:text-[#E23A28] p-1"
                            title="Remove contact"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
